import React, { useState } from "react";
import "./credibility-tool-styles.css";

// Simplified UI components
const Card = ({ children, className = "" }) => (
  <div className={`card ${className}`}>{children}</div>
);

const Button = ({ children, className = "", ...props }) => (
  <button className={`button ${className}`} {...props}>
    {children}
  </button>
);

const Input = (props) => <input className="input" {...props} />;

const Progress = ({ value }) => (
  <div className="progress">
    <div className="progress-bar" style={{ width: `${value}%` }}></div>
  </div>
);

const Badge = ({ children, variant = "default" }) => (
  <span className={`badge ${variant === "outline" ? "outline" : ""}`}>
    {children}
  </span>
);

// Main component
const CredibilityToolWireframes = () => {
  const [activeTab, setActiveTab] = useState("1");

  const TabContent = ({ value, title, description, children }) => (
    <div className={activeTab === value ? "block" : "hidden"}>
      <Card className="w-full max-w-3xl mx-auto">
        <h2 className="text-xl font-bold mb-2">{title}</h2>
        <p className="text-gray-600 mb-4">{description}</p>
        {children}
      </Card>
    </div>
  );

  return (
    <div className="p-4 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-4 text-center">Veracity AI Tools</h1>
      <div className="flex justify-center mb-4">
        {["1", "2", "3", "4", "5"].map((tab) => (
          <button
            key={tab}
            className={`tab-button ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            Version {tab}
          </button>
        ))}
      </div>

      <TabContent
        value="1"
        title="Claim Check"
        description="Verify and assess individual claims within content"
      >
        <div className="space-y-4">
          <Input placeholder="Enter article URL or paste content" />
          <Button className="w-full">Analyze Claims</Button>

          <Card>
            <h3 className="font-bold mb-2">
              Claim 1: "Global temperatures have risen by 1.1°C since
              pre-industrial times."
            </h3>
            <div className="flex justify-between items-center mb-2">
              <span>Verification Level:</span>
              <Badge variant="outline">High</Badge>
            </div>
            <Progress value={85} />
            <p className="text-sm text-gray-500 mt-2">
              Corroborated by multiple scientific studies and international
              climate reports.
            </p>
          </Card>

          <Card>
            <h3 className="font-bold mb-2">
              Claim 2: "Renewable energy will completely replace fossil fuels by
              2030."
            </h3>
            <div className="flex justify-between items-center mb-2">
              <span>Verification Level:</span>
              <Badge variant="outline">Low</Badge>
            </div>
            <Progress value={30} />
            <p className="text-sm text-gray-500 mt-2">
              Speculative claim not supported by current energy transition
              projections.
            </p>
          </Card>

          <div className="space-y-2">
            <h4 className="font-semibold">Overall Claim Check Metrics:</h4>
            <div className="flex items-center justify-between">
              <span>Claims Analyzed:</span>
              <Badge variant="secondary">15</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>Average Verification Level:</span>
              <Badge variant="secondary">Medium (62%)</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>Unverified Claims:</span>
              <Badge variant="secondary">3</Badge>
            </div>
          </div>

          <Button className="w-full outline">
            View Detailed Claim Analysis
          </Button>
        </div>
      </TabContent>

      <TabContent
        value="2"
        title="Source Tracker"
        description="Track and evaluate the sources behind each claim"
      >
        <div className="space-y-4">
          <Input placeholder="Enter article URL or paste content" />
          <Button className="w-full">Analyze Sources</Button>

          <Card>
            <h3 className="font-bold mb-2">
              Claim: "Electric vehicles produce 50% less CO2 over their lifetime
              compared to gas-powered cars."
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Source: International Energy Agency (IEA)</span>
                <Badge variant="outline">Highly Reliable</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span>Source: EV manufacturer's website</span>
                <Badge variant="outline">Potentially Biased</Badge>
              </div>
              <p className="text-sm text-gray-500">
                2 additional sources cited
              </p>
            </div>
          </Card>

          <div className="space-y-2">
            <h4 className="font-semibold">Overall Source Quality:</h4>
            <Progress value={75} />
            <div className="flex justify-between text-sm text-gray-500">
              <span>Low Quality</span>
              <span>High Quality</span>
            </div>
          </div>

          <Button className="w-full outline">
            Filter Claims by Source Quality
          </Button>
        </div>
      </TabContent>

      <TabContent
        value="3"
        title="Consensus Meter"
        description="Measure expert consensus on controversial claims"
      >
        <div className="space-y-4">
          <Input placeholder="Enter controversial claim" />
          <Button className="w-full">Check Expert Consensus</Button>

          <Card>
            <h3 className="font-bold mb-2">
              Claim: "Human activities are the primary driver of observed
              climate change."
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Expert Consensus:</span>
                <Badge variant="outline">Strong Agreement (97%)</Badge>
              </div>
              <Progress value={97} />
              <p className="text-sm text-gray-500">
                Based on analysis of 11,944 peer-reviewed climate science papers
              </p>
            </div>
          </Card>

          <div className="space-y-2">
            <h4 className="font-semibold">Consensus Breakdown:</h4>
            <div className="flex justify-between items-center">
              <span>Strongly Agree:</span>
              <Badge variant="secondary">90%</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span>Somewhat Agree:</span>
              <Badge variant="secondary">7%</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span>Disagree:</span>
              <Badge variant="secondary">3%</Badge>
            </div>
          </div>

          <Button className="w-full outline">View Full Consensus Report</Button>
        </div>
      </TabContent>

      <TabContent
        value="4"
        title="Rapid Fact Checker"
        description="Quickly verify claims against a database of known facts"
      >
        <div className="space-y-4">
          <Input placeholder="Enter a claim to fact-check" />
          <Button className="w-full">Rapid Fact Check</Button>

          <Card>
            <h3 className="font-bold mb-2">
              Claim: "The Great Wall of China is visible from space."
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Fact Check Result:</span>
                <Badge variant="outline">False</Badge>
              </div>
              <p className="text-sm text-gray-500">
                This is a common misconception. The Great Wall is not visible
                from space with the naked eye.
              </p>
              <p className="text-sm text-gray-500">
                Verified by NASA and multiple astronaut accounts.
              </p>
            </div>
          </Card>

          <div className="space-y-2">
            <h4 className="font-semibold">Fact Check Stats:</h4>
            <div className="flex justify-between items-center">
              <span>Database Entries Searched:</span>
              <Badge variant="secondary">1,000,000+</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span>Search Time:</span>
              <Badge variant="secondary">0.23 seconds</Badge>
            </div>
          </div>

          <Button className="w-full outline">Check Another Claim</Button>
        </div>
      </TabContent>

      <TabContent
        value="5"
        title="Manipulation Analyzer"
        description="Analyze persuasion techniques and potential manipulation in content"
      >
        <div className="space-y-4">
          <Input placeholder="Enter text to analyze" />
          <Button className="w-full">Analyze Content</Button>

          <Card>
            <h3 className="font-bold mb-2">Manipulation Results</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Manipulation Potential:</span>
                <Badge variant="outline">Moderate</Badge>
              </div>
              <Progress value={60} />
              <p className="text-sm text-gray-500">
                Several persuasion techniques detected. Exercise critical
                thinking.
              </p>
            </div>
          </Card>

          <div className="space-y-2">
            <h4 className="font-semibold">Detected Techniques:</h4>
            <div className="flex flex-wrap gap-2">
              <Badge>Emotional Appeal</Badge>
              <Badge>Loaded Language</Badge>
              <Badge>Urgency Creation</Badge>
              <Badge>Oversimplification</Badge>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold">Content Characteristics:</h4>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">High Emotional Charge</Badge>
              <Badge variant="outline">Low Nuance</Badge>
              <Badge variant="outline">Future-Focused</Badge>
              <Badge variant="outline">Anecdotal Evidence</Badge>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold">Additional Insights:</h4>
            <p className="text-sm text-gray-500">
              The content uses vivid imagery and personal stories to create an
              emotional impact. Consider seeking out more objective, data-driven
              sources for balance.
            </p>
          </div>

          <Button className="w-full outline">View Detailed Analysis</Button>
        </div>
      </TabContent>
    </div>
  );
};

// Export the app
const App = () => <CredibilityToolWireframes />;

export default App;
